"use client";

import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { formatPhoneNumberDisplay } from "@/app/_module/utils/phoneUtils";
import { Section } from "./overview/primitives";
import { Choice, ChoiceGroup } from "./overview/wizard-ui";

// Radix can't use "" as an item value, so "Choose for me" maps to this.
const AUTO = "auto";

/**
 * Sole prop only: choose which owned number becomes the texting number.
 *
 * This is the cheap moment to choose. After approval, switching means the
 * carriers register the new number from scratch, and texting stops for up to
 * 2-3 weeks. Skipping is fine: leaving it on "Choose for me" makes the backend
 * auto-pick the agent's most-called number.
 */
export default function StepTextingNumber() {
  const { watch, setValue } = useFormContext<any>();
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const selected: string = watch("preferredPhoneNumberId") || "";

  const owned = useMemo(
    () => (phoneNumbers as any[]).filter((p: any) => p?.status === "active"),
    [phoneNumbers],
  );

  // Default to auto so skipping the step is the no-op path.
  useEffect(() => {
    if (selected === undefined) setValue("preferredPhoneNumberId", "");
  }, [selected, setValue]);

  return (
    <Section
      id="a2p-texting-number-pick"
      title="Your texting number"
      description="Sole proprietor registrations text from one number. Pick it now, or let us choose."
    >
      {owned.length === 0 ? (
        <p className="text-sm text-neutral-500">
          No numbers on your account yet. We&apos;ll pick one automatically once you have one.
        </p>
      ) : (
        <ChoiceGroup
          aria-labelledby="a2p-texting-number-pick"
          value={selected || AUTO}
          onValueChange={(v) => setValue("preferredPhoneNumberId", v === AUTO ? "" : v)}
          className="sm:grid-cols-2"
        >
          <Choice value={AUTO} title="Choose for me" description="We'll use the number you call from most." />
          {owned.map((p: any) => {
            const stateName = (typeof p.state === "string" ? p.state : p.state?.name) || p.stateName || "";
            return (
              <Choice
                key={p.id}
                value={p.id}
                title={<span className="tabular-nums">{formatPhoneNumberDisplay(p.phoneNumber)}</span>}
                description={stateName || undefined}
              />
            );
          })}
        </ChoiceGroup>
      )}
      <p className="text-pretty text-xs leading-relaxed text-neutral-500">
        Choose carefully. You can switch later, but texting stops for up to 2-3 weeks while the carriers register the
        new number. Your other numbers keep making calls and receiving texts.
      </p>
    </Section>
  );
}
